/**
 * PF v2 — the card / SEPA place-order sequence (ISSUE-2928 step 3).
 *
 * Extracted from `PlaceOrderHandlerPublisher` so the ORDER OF OPERATIONS is
 * testable on its own. It is the part that has to be right on a money path:
 *
 *   1. refuse outright if no confirm handler is published. No handler means no
 *      Elements scope, which means the intent never opened — placing then would
 *      create an order that cannot be paid.
 *   2. POST the order.
 *   3. confirm the payment with Stripe, using the order id in the return URL.
 *
 * Placement comes BEFORE the confirm because the confirm's `return_url` carries
 * the order id that `PaymentStatus` reconciles against — the same order legacy
 * used. The intent reserved that public id server-side when it was opened
 * (`SessionPaymentService` sets `session.reservedOrderPublicId`), and v2
 * placement is idempotent on the session, so a retry cannot double-order.
 *
 * The residual exposure is unchanged from legacy: an order can exist unpaid if
 * the confirm fails after the POST. That is what `PaymentStatus` and the Stripe
 * webhook exist to resolve; the alternative (confirm first) would charge a card
 * for an order that may then fail to place, which is strictly worse.
 */

import { logger } from 'utils/logging'

import type { StripeConfirmHandler } from '../../store/slices/stepNavSlice'

export interface StripePlacementDeps {
  /** `useRevampPlaceOrder().placeOrder` — POSTs the v2 order. */
  placeOrder: () => Promise<{ data: { id: number } }>
  /** Published by the provider from inside Elements; `null` when there is none. */
  confirm: StripeConfirmHandler | null
  /** Store flag the Summary CTA disables itself on. */
  setPlacing: (placing: boolean) => void
  /** The order exists: called as soon as it is placed, before the payment is confirmed. */
  onPlaced?: (orderId: number) => void
  /** Order placed AND paid (or SEPA-processing). The client secret is needed to
   *  send the client on to `PaymentStatus`, which reads the payment back by it. */
  onConfirmed: (orderId: number, clientSecret: string) => void
  /** Anything went wrong, including the refusal in (1). */
  /** `isPlaced`: the order exists, so this attempt failed on the payment, not the placement. */
  onFailed: (error: unknown, isPlaced: boolean) => void
}

/**
 * Place a v2 order and confirm its Stripe payment.
 *
 * Never rejects: every outcome is reported through `onConfirmed` / `onFailed`, so
 * a caller wired to a button cannot leave the CTA stuck behind an unhandled
 * rejection.
 */
export async function placeAndConfirmStripeOrder({
  placeOrder,
  confirm,
  setPlacing,
  onPlaced,
  onConfirmed,
  onFailed,
}: StripePlacementDeps): Promise<void> {
  if (!confirm) {
    const error = new Error('pf-revamp: no Stripe confirm handler — the payment intent is not open')
    logger.error(error.message, undefined, {
      context: { component: 'placeAndConfirmStripeOrder', action: 'refuseWithoutIntent' },
    })
    onFailed(error, false)
    return
  }

  setPlacing(true)
  let isPlaced = false
  try {
    const response = await placeOrder()
    const orderId = response.data.id
    isPlaced = true
    onPlaced?.(orderId)
    const { clientSecret } = await confirm(orderId)
    logger.info('pf-revamp Stripe order placed and confirmed', undefined, {
      context: { component: 'placeAndConfirmStripeOrder', action: 'confirmed', metadata: { orderId } },
    })
    onConfirmed(orderId, clientSecret)
  } catch (error) {
    // A failure after placement is a payment failure, which `onFailed` reports with the order.
    if (!isPlaced) {
      logger.error('pf-revamp Stripe place-order failed', error, {
        context: { component: 'placeAndConfirmStripeOrder', action: 'placeAndConfirm' },
      })
    }
    onFailed(error, isPlaced)
  } finally {
    setPlacing(false)
  }
}
